import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../../../actions';
import {
    ACCEPT_CUSTOMER_PROJECT,
    AcceptCustomerProjectAction,
    CLOSE_PROJECT_BY_FREELANCER,
    CloseProjectByFreelancerAction,
    DECLINE_CUSTOMER_REQUEST,
    DeclineCustomerRequestAction,
    FETCH_CUSTOMERS_DATA,
    FetchCustomersDataAction,
    handleDeclineRequestError,
    releaseDeclineProjectPopup,
    showCustomersError,
    updateActiveProject,
    updateCustomerRequest,
    updateCustomersData
} from '../../../actions/freelancers/freelancer-customers';
import {
    mapCloseProjectByFreelancer,
    mapCustomerProject,
    mapCustomersFromQueryFormat
} from '../../../mappers/freelancers';
import { showNoticeMessage } from '../../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { NoticeType } from '../../../typings/grid';
import { State } from '../../../typings/state';

export const fetchFreelancerCustomersDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_CUSTOMERS_DATA).mergeMap(
        (action: FetchCustomersDataAction): Observable<Action> => {
            return Observable.fromPromise(deps.entitiesDataProvider.fetchFreelancerCustomers(action.login))
                .map(customersData => mapCustomersFromQueryFormat(customersData.data))
                .map(customers => updateCustomersData(customers))
                .catch(e => Observable.of(showCustomersError(e.errorType)));
        }
    );
};

export const acceptCustomerProjectEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(ACCEPT_CUSTOMER_PROJECT).mergeMap(
        (action: AcceptCustomerProjectAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.acceptServiceByFreelancer({
                    input: {
                        clientId: action.clientId,
                        projectId: action.projectId
                    }
                })
            )
                .map(response => mapCustomerProject(true, response.acceptFlServiceByFreelancer))
                .map(resolution => updateCustomerRequest(resolution))
                .catch(e => {
                    showNoticeMessage(NoticeType.error, e.getMessage());
                    return Observable.of(handleDeclineRequestError(action.clientId));
                });
        }
    );
};

export const declineCustomerRequestEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(DECLINE_CUSTOMER_REQUEST).mergeMap(
        (action: DeclineCustomerRequestAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.cancelServiceByFreelancer({
                    input: {
                        clientId: action.clientId,
                        projectId: action.projectId
                    }
                })
            )
                .map(response => mapCustomerProject(false, response.cancelFlServiceByFreelancer))
                .map(resolution => updateCustomerRequest(resolution))
                .catch(e => {
                    showNoticeMessage(NoticeType.error, e.getMessage());
                    return Observable.of(handleDeclineRequestError(action.clientId));
                });
        }
    );
};

export const closeProjectByFreelancer: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(CLOSE_PROJECT_BY_FREELANCER).mergeMap(
        (action: CloseProjectByFreelancerAction): Observable<Action> => {
            return Observable.fromPromise(
                deps.freelancerMutationExecutor.cancelServiceByFreelancer({
                    input: {
                        clientId: action.clientId,
                        projectId: action.projectId
                    }
                })
            )
                .map(response => mapCloseProjectByFreelancer(response.cancelFlServiceByFreelancer))
                .mergeMap(resolution =>
                    Observable.concat(
                        Observable.of(releaseDeclineProjectPopup()),
                        Observable.of(updateActiveProject(resolution))
                    )
                )
                .catch(e => {
                    showNoticeMessage(NoticeType.error, e.getMessage());
                    return Observable.of(releaseDeclineProjectPopup());
                });
        }
    );
};
